import { useNavigate, useParams } from "react-router-dom";
import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { ref as storageRef, getBlob } from "firebase/storage";
import { storage } from "../../firebase";
import {
  connectFunctionsEmulator,
  getFunctions,
} from "firebase/functions";
import {
  Typography,
  Card,
  CardHeader,
  CardBody,
  IconButton,
  Button,
  Carousel,
} from "@material-tailwind/react";
import RelistBreakdownDialog from "../../components/RelistBreakdownDialog";
import {
  ArrowUpIcon,
  ChartBarIcon,
  TicketIcon,
  CreditCardIcon,
  ClockIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import { StatisticsCard } from "widgets/cards/statistics-card";
import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import db from "../../firebase";
import { ArrowDownIcon, ArrowLeftCircleIcon, CalendarDaysIcon, PhoneIcon } from "@heroicons/react/24/solid";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import { formatFirebaseTimestamp, getUniquePriceHistoryChanges } from "utils";
import { formatPrice, friendlyModelName, friendlyTimeFormat, locationFromListingScrapeData } from "../../utils";
import { ListingDetailImage } from "../../components/ListingDetailImage";
import { ListingDetailsChart } from "../../components/ListingDetailsChart";
import PriceHistoryDialog from "./PriceHistoryDialog";
import { DataContext } from "../../context/DataContext";
import { ControllerStatisticsChart } from "../../components/ControllerStatisticsChart";

const options = {
  snapshotListenOptions: { includeMetadataChanges: true },
};
const col = "listing_aggregations_v1";

const functions = getFunctions();
  
if (window.location.href.indexOf('localhost') > -1) {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001)
}

function filterUrlsForMostRecent(urls){

  const cached = {};

  for(let i = 0; i< urls.length;i++){
    const link = urls[i].link;
    const dt = urls[i].dt;

    const spl = link.split('ppp')[1].split('?')[0];
    const name = `ppp_${spl}`;

    if (cached[name]){
      if (dt > cached[name].dt){
        cached[name] = urls[i];
      }
    } else {
      cached[name] = urls[i];
    }
  }
  const ret = Object.values(cached);
  // console.log(ret[0].dt.toDate())
  return ret;


}

const ListingDetails = ({ulid}) => {
  const { slug } = useParams();
  const uniqueListingId = slug ? slug : ulid;
  const queryListing = query(doc(db, `${col}/${uniqueListingId}`));
  const [listingData] = useDocument(queryListing, options);
  const dataContext = useContext(DataContext);

  const queryListingScrapes = useMemo(()=> query(
    collection(db, `${col}/${uniqueListingId}/listing_scrapes`),
    orderBy("scrapeTs", "asc"),
  ), [uniqueListingId]);
  
  const [listingScrapesData] = useCollection(queryListingScrapes, options);

  function timeseriesFromData(data) {
    let priceUSDOfLastPriceChange = data[0].data().price_usd;
    const points = data.map((item, index) => {
      const dt = item.data().scrapeTs.toDate();
      if (index !== 0){
        if (item.data().currency === data[index-1].data().currency){
          // use the last price_usd unless the underlying price has changed
          const price = item.data().price;
          const prevPrice = data[index-1].data().price;
          if (price !== prevPrice){
            priceUSDOfLastPriceChange = item.data().price_usd;
            // console.log(`price diff ${dt} ${price} ${prevPrice}`)
            return {dt, price: item.data().price_usd};
          } else {
            return {dt, price: priceUSDOfLastPriceChange};
          }
        } else {
          priceUSDOfLastPriceChange = item.data().price_usd;
          return {dt, price: item.data().price_usd};
        }
      } else {
        return {dt, price: item.data().price_usd};
      }
    });
    return points;
  }

  function allNaN(timeseries){
    return timeseries.filter(item => isNaN(item.price)).length === timeseries.length;
  }

  const navigate = useNavigate()

  const handleBack = ()=>{
    navigate(-1);
  }

  const lastListingScrape = () => {
    if (!listingScrapesData) return
    const latestListingScrapeData = listingScrapesData.docs[listingScrapesData.docs.length-1].data();
    return latestListingScrapeData;
  }

  const dealerInfo = () => {

    const ret = {
      dealerName: 'Loading...',
      dealerPhoneNumber: "Loading...",
      dealerLocation: "Loading..."
    }

    if (!listingData){
      return ret;
    }

    ret.dealerName = listingData.data().dealerName;
    ret.dealerPhoneNumber = listingData.data().dealerPhoneNumber
    ret.individualName = listingData.data().individualName;
    ret.dealerLocation = listingData.data().dealerLocation;

    
    if (ret.dealerName){
      ret.dealerName = ret.dealerName.replaceAll(`&amp;`,``);
      ret.dealerName = ret.dealerName.replaceAll(` ,`,`,`);
    }
    if (ret.individualName){
      ret.individualName = ret.individualName.replaceAll(`&amp;`,``);
      ret.individualName = ret.individualName.replaceAll(` ,`,`,`);
    }
    if (ret.dealerLocation){
      ret.dealerLocation = ret.dealerLocation.replaceAll(`&amp;`,``);
      ret.dealerLocation = ret.dealerLocation.replaceAll(` ,`,`,`);
    }
    return ret;
  }

  const [urls, setUrls] = useState();

  useEffect(() => {
    const path = `listing_aggregations_v1/${uniqueListingId}/listing_images/img`;
    const q = query(doc(db, path));
    
    getDoc(q).then((doc) => {
      if (doc.data()){
        // console.log(doc.data().urls);
        // filter urls to only use latest stored image name
        // check logic for storing images trade-a-plane, what happens when we detect a new images. fs
        const filteredForRecent = filterUrlsForMostRecent(doc.data().urls)
        setUrls(filteredForRecent);      
      } else{
        console.log(`doc does not exist`);
      }        
    });
  },[uniqueListingId]);

  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [relistDom, setRelistDom] = useState(null);
  const [relistDialogOpen, setRelistDialogOpen] = useState(false);

  // Look up relist-corrected DOM from webstats_cumulative
  // Match by listing ID within the cycle, not just reg (handles sold-and-relisted aircraft)
  useEffect(() => {
    if (!listingData) return;
    const reg = listingData.data()?.reg;
    if (!reg) return;
    const numericId = listingData.data()?.listingId;
    const cumQuery = query(
      collection(db, "webstats_cumulative"),
      where("reg", "==", reg),
    );
    getDocs(cumQuery).then((snap) => {
      let match = null;
      const allDocs = [];
      snap.forEach((d) => allDocs.push(d.data()));

      // Find the cycle this listing belongs to
      for (const data of allDocs) {
        if (data.relist_true_dom && data.relist_count > 1) {
          const cycleIds = data.relist_cycle_listing_ids || [];
          if (cycleIds.includes(numericId)) {
            // Collect all listings in this cycle
            const cycleListings = allDocs
              .filter((d) => cycleIds.includes(d.listing_id))
              .sort((a, b) => {
                const aDate = a.ws_creation_date?.toDate ? a.ws_creation_date.toDate() : a.ws_creation_date ? new Date(a.ws_creation_date) : new Date(0);
                const bDate = b.ws_creation_date?.toDate ? b.ws_creation_date.toDate() : b.ws_creation_date ? new Date(b.ws_creation_date) : new Date(0);
                return aDate - bDate;
              });
            match = {
              trueDom: data.relist_true_dom,
              relistCount: data.relist_count,
              cycleViews: data.relist_cycle_views,
              cycleImpressions: data.relist_cycle_impressions,
              cycleStart: data.relist_cycle_start,
              cycleEnd: data.relist_cycle_end,
              listings: cycleListings,
            };
            break;
          }
        } else if (!match && data.listing_id === numericId) {
          match = { trueDom: data.relist_true_dom || data.days_aging, relistCount: 1, listings: [data] };
        }
      }
      if (match) {
        setRelistDom(match);
      } else {
        // No webstats data — listing may be newer than last CSV upload
        // Show True DOM from scraper days_active instead
        const daysActive = listingData.data()?.days_active;
        if (daysActive != null) {
          setRelistDom({
            trueDom: Math.ceil(daysActive),
            relistCount: 1,
            listings: [],
            noWebStats: true,
          });
        }
      }
    }).catch(() => {});
  }, [listingData]);

  return (
    <>

      <PriceHistoryDialog open={priceHistoryOpen} setOpen={setPriceHistoryOpen} listingScrapesData={listingScrapesData}></PriceHistoryDialog>

      <div className="mb-0 p-0">

          <IconButton
            variant="text"
            onClick={handleBack}
            className="border-solid border-2 border-lma-dark"
          >
            <ArrowLeftCircleIcon className="h-7 w-7 text-lma-dark" />
          </IconButton>

      </div>

      <div className="mb-12 grid gap-y-5 gap-x-3 md:grid-cols-2 xl:grid-cols-2">

        <div className="col-span-1 h-full">

          {listingData && 
            <ListingDetailImage
            src={listingData.data().imageURL}
            price={
              formatPrice(listingData.data().price, listingData.data().currency)
            }
            title={listingData.data().title}
            reg={listingData.data().reg}
            site={listingData.data().source}
            listingURL={listingData.data().listingURL}
            lastSeen={listingData.data().last_seen}
            location={locationFromListingScrapeData(lastListingScrape())}
            country={listingData.data().country}
            year={listingData.data().year}
            model={friendlyModelName(dataContext, listingData.data().model)}
            uniqueListingId={listingData.data().uniqueListingId}
            bPath={lastListingScrape()?.bPath}
          />}
        </div>      
        {listingData && urls &&

        <div className="col-span-1">
            <Card className="h-full">
              <Carousel loop={true} autoplay={false} className="">
              {urls && urls.length && urls.map((m)=>{
                return <img
                src={m.link}
                key={m.link}
                alt=""
                className="h-full max-h-96 w-full object-cover"
              />
              })}
              </Carousel>
            </Card>
        </div> }
      </div>
      
      <div className="mb-12 grid gap-y-8 gap-x-3 md:grid-cols-3 xl:grid-cols-3">

      {listingData && listingData.data().timeState && (
        <StatisticsCard
          key={"timing"}
          value={
            listingData.data().timeState.totalTime
          }
          title={"Total Time"}
          icon={React.createElement(ClockIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <>  
              {/* <Typography className="font-normal text-sm text-lma-gray">
                Number of Engines: {listingData.data().timeState.numberOfEngines}
              </Typography> */}
              {(listingData.data().timeState.numberOfEngines === 1) &&
                <>
                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine Time: {listingData.data().timeState.engine1Time}
                  </Typography>
                </>}
              {(listingData.data().timeState.numberOfEngines === 2) &&
                <>
                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine 1: {listingData.data().timeState.engine1Time}
                  </Typography>

                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine 2: {listingData.data().timeState.engine2Time}
                  </Typography>
                </>}
            </>
         }
        />)}


      {listingData && (
        <StatisticsCard
          key={"dealer"}
          value={
            dealerInfo().dealerPhoneNumber
          }
          title={"Dealer"}
          icon={React.createElement(PhoneIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <>  
              <Typography className="font-normal text-sm text-lma-gray">
              {dealerInfo().dealerName}
              </Typography>
              <Typography className="font-normal text-sm text-lma-gray">
                Contact: {dealerInfo().individualName}
              </Typography>
              <Typography className="font-normal text-sm text-lma-gray">
                Location: {dealerInfo().dealerLocation}
              </Typography>
            </>
         }
        />)}

      {listingData && listingScrapesData  &&(
        <StatisticsCard
          key={"price"}
          //   rest={"jello"}
          value={
            listingData ? formatPrice(listingData.data().price, listingData.data().currency) : "loading..."
          }
          title={"Current Price"}
          icon={React.createElement(CreditCardIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <div>
            <Typography className="font-normal text-sm text-lma-gray">
              {"Orignal Price "}
              <strong className={"text-blue-gray"}>
              {listingData ? formatPrice(listingScrapesData.docs[0].data().price, listingScrapesData.docs[0].data().currency) : "loading..."}</strong>
            </Typography>
            <Typography className="font-normal text-sm text-lma-gray">
              {"active for "}
              <strong className={"text-blue-gray"}>
                {Math.ceil(listingData.data().days_active)}</strong>&nbsp;{ Math.ceil(listingData.data().days_active) === 1 ? "day" : "days"}
            </Typography>
            </div>
          }
        />)}

      {relistDom && (
        <div>
          <StatisticsCard
            key={"true_dom"}
            value={
              <>
                <div>
                  <span>{relistDom.trueDom} days</span>
                  {relistDom.relistCount > 1 && (
                    <span className="text-amber-700 text-sm ml-1">({relistDom.relistCount} listings)</span>
                  )}
                </div>
                {listingData && !relistDom?.noWebStats && (
                  <Typography className="font-normal text-sm text-gray-600">
                    This listing: {Math.ceil(listingData.data().days_active)}d
                  </Typography>
                )}
                {relistDom?.noWebStats && (
                  <Typography className="font-normal text-xs text-gray-400 italic">
                    From scraper — no web stats data yet
                  </Typography>
                )}
              </>
            }
            title={"True DOM"}
            icon={React.createElement(CalendarDaysIcon, {
              className: "w-6 h-6 text-white",
            })}
            footer={
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outlined"
                  className="py-1 px-3 text-xs"
                  onClick={() => setRelistDialogOpen(true)}
                >
                  Details
                </Button>
              </div>
            }
          />
        </div>
      )}

      {listingData && (<StatisticsCard
          key={"price_change"}
          //   rest={"jello"}
          value={
            listingData ? formatPrice(listingData.data().price_change_usd, "USD") : "loading..."
          }
          title={"Price Change vs. original"}
          icon={React.createElement(ChartBarIcon, {
            className: "w-6 h-6 text-red",
          })}
          footer={
            <Typography className="font-normal text-sm text-lma-gray">
              <strong
                className={
                  listingData && listingData.data().price_change_pct_usd >= 0
                    ? "text-green-500"
                    : "text-red-500"
                }
              >
                {Math.abs(listingData.data().price_change_pct_usd) > 0 ? Math.ceil(listingData.data().price_change_pct_usd * 100) + "%" : ""}
              </strong>
              &nbsp;
              {Math.abs(listingData.data().price_change_pct_usd) > 0  ? (listingData.data().price_change_pct_usd >= 0 ? "increase" : "decrease"): ""}
              {Math.abs(listingData.data().price_change_pct_usd) === 0  ? "No change" : ""}
            </Typography>
          }
        />)}

        {listingData && (
          <StatisticsCard

            key={"price_changes"}
            value={
              friendlyTimeFormat(listingData.data().last_change_dt)
            }
            title={"Since last price change"}
            icon={React.createElement(CalendarDaysIcon, {
              className: "w-6 h-6 text-white",
            })}
            footer={
              <Typography className="font-normal text-sm text-lma-gray">
                <strong>
                  {listingData.data().no_price_changes}
                </strong>
                &nbsp;
                {(listingData.data().no_price_changes > 1) || (listingData.data().no_price_changes === 0)
                  ? "price changes"
                  : "price change"}
              </Typography>
            }
          />
        )}
      </div>

      {listingData && <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-10">
       
      <div className="xl:col-span-3 col-span-1">
        <Card className="h-full">
          <CardHeader
            floated={false}
            shadow={false}
            color="transparent"
            className="m-0 p-6"
          >
            <Typography variant="h6" className="text-lma-dark mb-2">
              Price Changes
            </Typography>

            <IconButton
              size="sm"
              variant="text"
              onClick={(e) => setPriceHistoryOpen(true)}
              className="!absolute top-5 right-5 w-5 h-5 "
            >
              <InformationCircleIcon className="w-5 h-5" />
            </IconButton> 

          </CardHeader>
          {listingScrapesData && 
          <CardBody>
           {
              getUniquePriceHistoryChanges(listingScrapesData.docs).map(
                ({ price_usd, currency, scrapeTs, change }, key) => (
                  <div key={key} className="flex items-start gap-4 py-3">
                    <div
                      className={`relative p-1 after:absolute after:-bottom-6 after:left-2/4 after:w-0.5 after:-translate-x-2/4 after:bg-gray-200 after:content-[''] ${
                        key === listingScrapesData.docs.length - 1
                          ? "after:h-0"
                          : "after:h-4/6"
                      }`}
                    >
                    
                      {change === 1 && (
                        <ArrowUpIcon
                          strokeWidth={3}
                          className="h-5 w-5 text-green-500"
                        />
                      )}
                      {change === -1 && (
                        <ArrowDownIcon
                          strokeWidth={5}
                          className="h-5 w-5 text-red-500"
                        />
                      )}
                      {change === 0 && (
                        <TicketIcon
                          strokeWidth={5}
                          className="h-5 w-5 text-green-500"
                        />
                      )}
                    </div>
                    <div>
                      <Typography
                        className="text-lma-dark block font-medium text-lg"
                      >
                        {formatPrice(price_usd, currency)}
                      </Typography>
                      <Typography
                        as="span"

                        className="text-md font-medium text-lma-gray"
                      >
                        {formatFirebaseTimestamp(scrapeTs)}
                      </Typography>
                    </div>
                  </div>
                )
                      )}
          </CardBody>}
        </Card>
        </div>

      <div className="col-span-1 h-full xl:col-span-7">
              <Card className="h-full">
              <CardHeader
            floated={false}
            shadow={false}
            color="transparent"
            className="m-0 flex items-center justify-between p-6"
          >
            <div>
              <Typography variant="h6" className="text-lma-dark mb-1">
                Chart
              </Typography>
            </div>
          </CardHeader>
              <CardBody>
            {listingScrapesData && (!allNaN(timeseriesFromData(listingScrapesData.docs)) ? (
                <ListingDetailsChart
                  timeseries={timeseriesFromData(listingScrapesData.docs)}
                  priceChange={listingData.data().price_change_usd}
                  price={listingData.data().price}
                ></ListingDetailsChart>
              ) :   <Typography
              variant="small"
              className="text-[11px] font-medium uppercase text-lma-gray"
            >
              Data unavailable
            </Typography>)}
              </CardBody>
          </Card>
            
        </div>

        {listingData.data().source === `controller` && listingData.data().dealerName === `Lone Mountain Aircraft` 
          && <div className="col-span-1 h-full xl:col-span-12">
            {listingScrapesData ? 
                <ControllerStatisticsChart listingId={listingData?.data().listingId}></ControllerStatisticsChart>
               :   <Typography
              variant="small"
              className="text-[11px] font-medium uppercase text-lma-gray"
            >
              Data unavailable
            </Typography>}
            
            
        </div>}
        
      </div>}

      {/* Specifications Section */}
      {listingScrapesData && listingScrapesData.docs.length > 0 && (
        <ListingSpecifications
          scrapeData={lastListingScrape()}
          source={listingData?.data()?.source}
          listingData={listingData?.data()}
        />
      )}

      <RelistBreakdownDialog
        open={relistDialogOpen}
        onClose={() => setRelistDialogOpen(false)}
        relistDom={relistDom}
        reg={listingData?.data()?.reg}
        title={listingData?.data()?.title}
        currentListingId={listingData?.data()?.listingId}
      />

    </>
  );
};

function SpecSection({ title, content }) {
  if (!content || (typeof content === "string" && !content.trim())) return null;
  return (
    <div className="mb-4">
      <Typography variant="h6" className="text-lma-dark mb-1 text-sm uppercase tracking-wide">
        {title}
      </Typography>
      <Typography className="text-sm text-lma-gray whitespace-pre-wrap leading-relaxed">
        {content}
      </Typography>
    </div>
  );
}

function SpecField({ label, value }) {
  if (!value || (typeof value === "string" && !value.trim())) return null;
  return (
    <div className="flex py-1.5 border-b border-gray-100 last:border-b-0">
      <span className="text-sm text-lma-gray font-medium w-44 shrink-0">{label}</span>
      <span className="text-sm text-lma-dark">{value}</span>
    </div>
  );
}

function ListingSpecifications({ scrapeData, source, listingData }) {
  const [specData, setSpecData] = useState(null);
  const [specLoading, setSpecLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadSpecs = useCallback(() => {
    if (!scrapeData?.bPath || !source || specData) return;
    setExpanded(true);
    if (source === "controller") {
      setSpecLoading(true);
      const fetchBlob = async (path) => {
        try {
          const sRef = storageRef(storage, path);
          const blob = await getBlob(sRef);
          const buf = await blob.arrayBuffer();
          const uint8 = new Uint8Array(buf);

          let text;
          if (uint8[0] === 0x1f && uint8[1] === 0x8b) {
            const ds = new DecompressionStream("gzip");
            const decompressed = new Blob([buf]).stream().pipeThrough(ds);
            text = await new Response(decompressed).text();
          } else {
            text = new TextDecoder().decode(uint8);
          }
          return JSON.parse(text);
        } catch (e) {
          console.warn("Failed to fetch blob:", path, e);
          return null;
        }
      };

      const load = async () => {
        try {
          const json2Path = scrapeData.bPath.replace(/_json$/, "_json2");
          const json2 = await fetchBlob(json2Path);

          const specFields = {};
          const specsArray = json2?.SpecsExceptDescription || [];
          specsArray.forEach((s) => {
            if (s.Value) {
              specFields[s.LabelDisplayText || s.SpecField] = s.Value;
            }
          });

          setSpecData({
            specs: specFields,
            description: json2?.DescriptionSpec?.Value || "",
          });
        } catch (e) {
          console.error("Failed to load specifications:", e);
        } finally {
          setSpecLoading(false);
        }
      };
      load();
    } else if (["planecheck", "globalair", "cirrus"].includes(source)) {
      // These sources need server-side parsing due to complex HTML/JSON blobs
      setSpecLoading(true);
      const load = async () => {
        try {
          const { httpsCallable } = await import("firebase/functions");
          const getSpecs = httpsCallable(functions, "getListingSpecifications");
          const result = await getSpecs({ bPath: scrapeData.bPath, source });
          setSpecData(result.data);
        } catch (e) {
          console.error("Failed to load specifications:", e);
        } finally {
          setSpecLoading(false);
        }
      };
      load();
    } else if (["tradeaplane"].includes(source)) {
      setSpecLoading(true);
      const fetchHtmlBlob = async (path) => {
        try {
          const sRef = storageRef(storage, path);
          const blob = await getBlob(sRef);
          const buf = await blob.arrayBuffer();
          const uint8 = new Uint8Array(buf);
          if (uint8[0] === 0x1f && uint8[1] === 0x8b) {
            const ds = new DecompressionStream("gzip");
            const decompressed = new Blob([buf]).stream().pipeThrough(ds);
            return await new Response(decompressed).text();
          }
          return new TextDecoder().decode(uint8);
        } catch (e) {
          console.warn("Failed to fetch blob:", path, e);
          return null;
        }
      };

      const load = async () => {
        try {
          const html = await fetchHtmlBlob(scrapeData.bPath);
          if (!html) { setSpecLoading(false); return; }
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          if (source === "tradeaplane") {
            setSpecData({
              specs: {},
              description: doc.querySelector("#detailed_desc pre")?.textContent?.trim() || "",
              airframe: doc.querySelector("#airframe pre")?.textContent?.trim() || "",
              avionics_equipment: doc.querySelector("#avionics_equipment pre")?.textContent?.trim() || "",
              interior_exterior: doc.querySelector("#interior_exterior pre")?.textContent?.trim() || "",
              engines_mods: doc.querySelector("#engines_mods pre")?.textContent?.trim() || "",
              remarks: doc.querySelector("#remarks pre")?.textContent?.trim() || "",
            });
          }
        } catch (e) {
          console.error("Failed to parse specifications:", e);
        } finally {
          setSpecLoading(false);
        }
      };
      load();
    }
  }, [scrapeData?.bPath, source, specData]);

  if (!scrapeData) return null;

  const renderControllerSpecs = () => {
    if (specLoading) return <Typography className="text-sm text-lma-gray">Loading specifications...</Typography>;
    if (!specData) return <Typography className="text-sm text-lma-gray">No specification data available.</Typography>;

    const specs = specData.specs || {};
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {Object.entries(specs).map(([label, value], i) => (
            <SpecField key={i} label={label} value={value} />
          ))}
          <SpecField label="Dealer" value={listingData?.dealerName} />
          <SpecField label="Contact" value={listingData?.individualName} />
          <SpecField label="Location" value={listingData?.dealerLocation} />
        </div>
        <SpecSection title="Description" content={specData.description} />
      </>
    );
  };

  const renderPlanecheckSpecs = () => {
    if (specLoading) return <Typography className="text-sm text-lma-gray">Loading specifications...</Typography>;
    if (!specData) return <Typography className="text-sm text-lma-gray">No specification data available.</Typography>;

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {Object.entries(specData.specs || {}).map(([label, value], i) => (
            <SpecField key={i} label={label} value={value} />
          ))}
          <SpecField label="Total Time" value={listingData?.timeState?.totalTime} />
          <SpecField label="Engine 1 Time" value={listingData?.timeState?.engine1Time} />
          <SpecField label="Engine 2 Time" value={listingData?.timeState?.engine2Time} />
          <SpecField label="Dealer" value={specData.dealer} />
          <SpecField label="Contact" value={specData.contact} />
          <SpecField label="Phone" value={specData.phone} />
        </div>
        <SpecSection title="Description" content={specData.description} />
        <SpecSection title="Avionics" content={specData.avionics} />
        <SpecSection title="Options" content={specData.options} />
        <SpecSection title="Interior" content={specData.interior} />
        <SpecSection title="Exterior" content={specData.exterior} />
        <SpecSection title="Remarks" content={specData.remarks} />
        <SpecSection title="Airplane Time State" content={specData.airplaneTimeState} />
      </>
    );
  };

  const renderTradeAPlaneSpecs = () => {
    if (specLoading) return <Typography className="text-sm text-lma-gray">Loading specifications...</Typography>;
    if (!specData) return <Typography className="text-sm text-lma-gray">No specification data available.</Typography>;

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {Object.entries(specData.specs || {}).map(([label, value], i) => (
            <SpecField key={i} label={label} value={value} />
          ))}
          <SpecField label="Total Time" value={listingData?.timeState?.totalTime} />
          <SpecField label="Engine 1 Time" value={listingData?.timeState?.engine1Time} />
          <SpecField label="Engine 2 Time" value={listingData?.timeState?.engine2Time} />
          <SpecField label="Dealer" value={specData.dealer} />
          <SpecField label="Contact" value={specData.contact} />
          <SpecField label="Phone" value={specData.phone} />
          <SpecField label="Location" value={specData.location} />
        </div>
        <SpecSection title="Description" content={specData.description} />
        <SpecSection title="Airframe" content={specData.airframe} />
        <SpecSection title="Avionics & Equipment" content={specData.avionics_equipment} />
        <SpecSection title="Interior / Exterior" content={specData.interior_exterior} />
        <SpecSection title="Engines & Modifications" content={specData.engines_mods} />
        <SpecSection title="Remarks" content={specData.remarks} />
      </>
    );
  };

  const renderGlobalairSpecs = () => {
    if (specLoading) return <Typography className="text-sm text-lma-gray">Loading specifications...</Typography>;
    if (!specData) return <Typography className="text-sm text-lma-gray">No specification data available.</Typography>;

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {Object.entries(specData.specs || {}).map(([label, value], i) => (
            <SpecField key={i} label={label} value={value} />
          ))}
          <SpecField label="Total Time" value={listingData?.timeState?.totalTime} />
          <SpecField label="Dealer" value={specData.dealer} />
          <SpecField label="Contact" value={specData.contact} />
          <SpecField label="Phone" value={specData.phone} />
          <SpecField label="Location" value={specData.location} />
        </div>
        <SpecSection title="Summary" content={specData.summary} />
        <SpecSection title="Avionics" content={specData.avionics} />
        <SpecSection title="Interior" content={specData.interior} />
        <SpecSection title="Exterior" content={specData.exterior} />
        <SpecSection title="Maintenance" content={specData.maintenance} />
        <SpecSection title="Features & Options" content={specData.features} />
        <SpecSection title="Description" content={specData.description} />
      </>
    );
  };

  const renderCirrusSpecs = () => {
    if (specLoading) return <Typography className="text-sm text-lma-gray">Loading specifications...</Typography>;
    if (!specData) return <Typography className="text-sm text-lma-gray">No specification data available.</Typography>;

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {Object.entries(specData.specs || {}).map(([label, value], i) => (
            <SpecField key={i} label={label} value={value} />
          ))}
          <SpecField label="Total Time" value={listingData?.timeState?.totalTime} />
          <SpecField label="Dealer" value={listingData?.dealerName} />
          <SpecField label="Contact" value={listingData?.individualName} />
          <SpecField label="Phone" value={listingData?.dealerPhoneNumber} />
          <SpecField label="Location" value={listingData?.dealerLocation} />
          <SpecField label="Certified" value={specData.certified} />
        </div>
        {Object.entries(specData.sections || {}).map(([title, content], i) => (
          <SpecSection key={i} title={title} content={content} />
        ))}
        <SpecSection title="Description" content={specData.description} />
        <SpecSection title="Interior" content={specData.interior} />
        <SpecSection title="Exterior" content={specData.exterior} />
        <SpecSection title="Features" content={specData.features} />
      </>
    );
  };

  const renderAircraftPostSpecs = () => {
    const cd = scrapeData.customData || {};
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <div>
            <SpecField label="Registration" value={listingData?.reg} />
            <SpecField label="Year" value={listingData?.year} />
            <SpecField label="Serial Number" value={scrapeData.serialNumber} />
            <SpecField label="Total Time" value={listingData?.timeState?.totalTime} />
            <SpecField label="Total Landings" value={cd.totalLandings} />
            <SpecField label="Passengers" value={cd.passengers} />
            <SpecField label="Certification" value={cd.certification} />
          </div>
          <div>
            <SpecField label="Damage History" value={cd.damageHistory} />
            <SpecField label="Engine Program" value={cd.engineProgram} />
            <SpecField label="APU Program" value={cd.apuProgram} />
            <SpecField label="Interior (Years)" value={cd.interiorYears} />
            <SpecField label="Paint (Years)" value={cd.paintYears} />
            <SpecField label="CPO" value={cd.cpo} />
            <SpecField label="CPO Date" value={cd.cpoDate} />
            <SpecField label="Prior Owners" value={cd.priorOwners} />
          </div>
        </div>
        {cd.equipment && cd.equipment.length > 0 && (
          <div className="mt-3">
            <Typography variant="h6" className="text-lma-dark mb-2 text-sm uppercase tracking-wide">
              Equipment
            </Typography>
            <div className="flex flex-wrap gap-1.5">
              {cd.equipment.map((item, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-lma-light border border-gray-200 text-lma-dark">
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  if (!expanded) {
    return (
      <div className="mt-6 mb-12 text-center">
        <Button
          variant="outlined"
          color="blue-gray"
          size="sm"
          onClick={loadSpecs}
          className="font-normal"
        >
          View Full Specifications
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 mb-12">
      <Card>
        <CardHeader
          floated={false}
          shadow={false}
          color="transparent"
          className="m-0 p-6"
        >
          <Typography variant="h6" className="text-lma-dark">
            Specifications
          </Typography>
          <Typography className="text-xs text-lma-gray mt-1">
            Full record from {source || "listing source"}
          </Typography>
        </CardHeader>
        <CardBody className="pt-0 px-6 pb-6">
          {source === "controller" && renderControllerSpecs()}
          {source === "planecheck" && renderPlanecheckSpecs()}
          {source === "tradeaplane" && renderTradeAPlaneSpecs()}
          {source === "globalair" && renderGlobalairSpecs()}
          {source === "aircraftpost" && renderAircraftPostSpecs()}
          {source === "cirrus" && renderCirrusSpecs()}
          {!["controller", "planecheck", "tradeaplane", "globalair", "aircraftpost", "cirrus"].includes(source) && (
            <Typography className="text-sm text-lma-gray">
              Specification data not available for this source.
            </Typography>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default ListingDetails;
